'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Image service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new ImageService object.
   *
   * Argument 'imageServiceUrl' - The URL to the Image service.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new ImageService object.
   */
  atex.aceui.service.ImageService = function(imageServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Returns an image service URL for the image content and image file represented by the given input object.
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.workspace - the workspace to use when retrieving the image. Mutually exclusive with transferInfo.view
       * @param {string} transferInfo.view - the view to use when retrieving the image. Mutually exclusive with transferInfo.workspace
       * @param {string} transferInfo.alias - the alias of the image content to create the URL for. Mutually exclusive with transferInfo.version
       * @param {string} transferInfo.version - the version of the image content to create the URL for. Mutually exclusive with transferInfo.alias.
       * If set, parameters transferInfo.workspace and transferInfo.view will be ignored
       * @param {string} transferInfo.fileName - the file name of the image file on the image content
       * @param {string} transferInfo.width - the requested width of the image, in pixels
       * @param {string} transferInfo.height - the requested height of the image, in pixels
       * @param {string} transferInfo.mode - the requested resize mode to use (f.e. 'FIT') for the image
       * @param {string} transferInfo.crop - the requested crop rectangle for the image
       * @param {string} transferInfo.aspectRatio - the requested aspect ratio for the image
       * @param {string} transferInfo.format - the name for the request format for the image
       * @param {string} transferInfo.quality - the requested quality for the image
       * @param {string} transferInfo.authToken - the authorization token to use for the image request
       *
       * @returns {Promise.<{string}, atex.aceui.error.Error>} - the image URL
       */
      getImageUrl: function (transferInfo) {
        var fileName = transferInfo.fileName || 'image.jpg';

        if (typeof transferInfo.workspace !== 'undefined' && typeof transferInfo.view !== 'undefined') {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Parameters workspace and view are mutually exclusive!',
            type: atex.aceui.error.types.INVALID_PARAMETERS
          });

          return q.reject(error);
        }

        if (typeof transferInfo.version !== 'undefined' && typeof transferInfo.alias !== 'undefined') {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Parameters version and alias are mutually exclusive!',
            type: atex.aceui.error.types.INVALID_PARAMETERS
          });

          return q.reject(error);
        }

        var imageParams = {
          w: transferInfo.width,
          h: transferInfo.height,

          m: transferInfo.mode,

          c: transferInfo.crop,
          a: transferInfo.aspectRatio,
          f: transferInfo.format,

          q: transferInfo.quality
        };

        let path = imageServiceUrl;

        if (typeof transferInfo.version !== 'undefined') {
          path += '/version/' + transferInfo.version + '/' + fileName;
        } else if (typeof transferInfo.workspace !== 'undefined') {
          path += '/workspace/' + transferInfo.workspace + '/alias/' + transferInfo.alias + '/' + fileName;
        } else if (typeof transferInfo.view !== 'undefined') {
          path += '/view/' + transferInfo.view + '/alias/' + transferInfo.alias + '/' + fileName;
        } else {
          path += '/alias/' + transferInfo.alias + '/' + fileName;
        }

        if (transferInfo.authToken) {
          imageParams.token = transferInfo.authToken;
        }

        return q.when(atex.aceui.HTTPUtils.withUrlParameters(path, imageParams));
      },

      /**
       * Retrieves an image from the Image service and returns it as a
       * blob (as defined by https://developer.mozilla.org/en-US/docs/Web/API/Blob).
       *
       * @param {Object} transferInfo - a transfer info object containing request details
       * @param {string} transferInfo.alias - the alias of the image content of the image to retrieve. Mutually exclusive with transferInfo.fileUri
       * @param {string} transferInfo.fileUri - the file service URI of the image to retrieve. Mutually exclusive with transferInfo.alias
       * @param {string} transferInfo.fileName - the file name of the image file on the image content. Only applicable if transferInfo.alias is specified
       * @param {string} transferInfo.width - the requested width of the image, in pixels
       * @param {string} transferInfo.height - the requested height of the image, in pixels
       * @param {string} transferInfo.mode - the requested resize mode to use (f.e. 'FIT') for the image
       * @param {string} transferInfo.crop - the requested crop rectangle for the image
       * @param {string} transferInfo.aspectRatio - the requested aspect ratio for the image
       * @param {string} transferInfo.format - the name for the request format for the image
       * @param {string} transferInfo.quality - the requested quality for the image
       * @param {string} transferInfo.imageMimeType - the requested image mime type for the image
       * @param {string} transferInfo.authToken - the authorization token to use for the image request
       *
       * @returns {Promise.<{BLOB}, atex.aceui.error.Error>} - the image, as a BLOB
       */
      getImage: function(transferInfo) {
        var deferred = q.defer(), error;

        if (typeof transferInfo.alias !== 'undefined' && typeof transferInfo.fileUri !== 'undefined') {
          error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Parameters alias and fileUri are mutually exclusive!',
            type: atex.aceui.error.types.INVALID_PARAMETERS
          });

          deferred.reject(error);

          return deferred.promise;
        }

        if (typeof transferInfo.fileUri !== 'undefined' && !atex.aceui.FileServiceUtils.isValidUri(transferInfo.fileUri)) {
          error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Not a valid File Service URI: ' + transferInfo.fileUri,
            type: atex.aceui.error.types.INVALID_PARAMETERS
          });

          deferred.reject(error);

          return deferred.promise;
        }

        var fullUrl = undefined;

        if (typeof transferInfo.fileUri !== 'undefined') {
          var uri = atex.aceui.FileServiceUtils.parseUri(transferInfo.fileUri);
          fullUrl = imageServiceUrl + '/' + uri.space + '/' + uri.owner + uri.path;
        } else {
          var fileName = transferInfo.fileName || 'image.jpg';
          fullUrl = imageServiceUrl + '/alias/' + transferInfo.alias + '/' + fileName;
        }

        var imageParams = {
          w: transferInfo.width,
          h: transferInfo.height,

          m: transferInfo.mode,

          c: transferInfo.crop,
          a: transferInfo.aspectRatio,
          f: transferInfo.format,

          q: transferInfo.quality
        };

        fullUrl = atex.aceui.HTTPUtils.withUrlParameters(fullUrl, imageParams);

        var xhr = $.ajaxSettings.xhr();

        xhr.open('GET', fullUrl, true);
        xhr.responseType = 'blob';

        if (typeof transferInfo.authToken !== 'undefined') {
          xhr.setRequestHeader('X-Auth-Token', transferInfo.authToken);
        }

        if (typeof transferInfo.imageMimeType !== 'undefined') {
          xhr.setRequestHeader('Accept', transferInfo.imageMimeType);
        }

        xhr.onload = function(e) {
          if (this.status === 200) {
            deferred.resolve({
              image: xhr.response,

              originalDimensions: {
                width: atex.aceui.HTTPUtils.getAsNumberOrNull(xhr.getResponseHeader('X-Original-Image-Width')),
                height: atex.aceui.HTTPUtils.getAsNumberOrNull(xhr.getResponseHeader('X-Original-Image-Height'))
              },

              renderedDimensions: {
                width: atex.aceui.HTTPUtils.getAsNumberOrNull(xhr.getResponseHeader('X-Rendered-Image-Width')),
                height: atex.aceui.HTTPUtils.getAsNumberOrNull(xhr.getResponseHeader('X-Rendered-Image-Height'))
              }
            });
          } else {
            var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.ImageService', {
              401: 'Could not authenticate user while reading working copy image from server!',
              403: 'Permission denied while reading working copy image from server!',
              404: 'Image was not found on the server!',

              'default': 'There was an error while reading working copy image from server!'
            });

            deferred.reject(error);
          }
        };

        xhr.onerror = function(event) {
          var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.ImageService', {
            401: 'Could not authenticate user while reading working copy image from server!',
            403: 'Permission denied while reading working copy image from server!',
            404: 'Image was not found on the server!',

            'default': 'There was an error while reading working copy image from server!'
          });

          deferred.reject(error);
        };

        xhr.ontimeout = function(event) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Error while reading working copy image from server: request timed out!',
            type: atex.aceui.error.types.TIMEOUT
          });

          deferred.reject(error);
        };

        xhr.onabort = function(event) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageService',
            cause: 'Error while reading working copy image from server: request aborted!',
            type: atex.aceui.error.types.OPERATION_CANCELLED
          });

          deferred.reject(error);
        };

        xhr.send();

        return deferred.promise;
      }
    };
  };
})(jQuery);
